import React, {FC} from "react";
import {Alert, Button, Result, Space, Typography} from "antd";
import dayjs from "dayjs";
import {PaymentSummary, sumSummaryItems} from "./PaymentSummary";
import {useRegistrationCopy} from "./registrationCopy";
import type {PublicRegistrationResponse, PublicRegistrationSubmissionContext} from "./types";

export type ResendConfirmationNotice = {
    type: "success" | "error";
    message: string;
};

// Success screen after an individual registration: summary, confirmation-link expiry and follow-up actions.
export const RegistrationResult: FC<{
    submission: PublicRegistrationResponse;
    context: PublicRegistrationSubmissionContext | null;
    resending: boolean;
    resendNotice: ResendConfirmationNotice | null;
    onResendConfirmation: () => void;
    onStartAnother: () => void;
}> = ({submission, context, resending, resendNotice, onResendConfirmation, onStartAnother}) => {
    const {labels} = useRegistrationCopy();
    const hasPaymentItems = sumSummaryItems(context?.summaryItems ?? []) > 0;

    return (
        <Result
            status="success"
            title={labels.submittedTitle}
            subTitle={hasPaymentItems ? labels.submittedSubTitle : labels.submittedSubTitleNoPayment}
            extra={(
                <div className="public-registration-result-content">
                    {context ? <PaymentSummary context={context}/> : null}

                    {submission.verificationTokenExpiresAtUtc ? (
                        <div className="public-registration-result-facts">
                            <div>
                                <Typography.Text>{labels.verificationExpires}</Typography.Text>
                                <Typography.Title level={4}>
                                    {dayjs(submission.verificationTokenExpiresAtUtc).format("YYYY-MM-DD HH:mm")}
                                </Typography.Title>
                            </div>
                        </div>
                    ) : null}

                    {resendNotice ? (
                        <Alert
                            type={resendNotice.type}
                            showIcon
                            message={resendNotice.message}
                        />
                    ) : null}

                    <Space wrap className="public-registration-result-actions">
                        {submission.verificationRequired && context?.email ? (
                            <Button
                                loading={resending}
                                onClick={onResendConfirmation}
                            >
                                {resending ? labels.resendingConfirmationEmail : labels.resendConfirmationEmail}
                            </Button>
                        ) : null}
                        <Button onClick={onStartAnother}>
                            {labels.backToForm}
                        </Button>
                    </Space>
                </div>
            )}
        />
    );
};
